import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

interface TestTrendsChartProps {
  componentName: string;
  historicalData: Array<{
    date: string;
    value: number;
    referenceMin?: number;
    referenceMax?: number;
  }>;
  unit?: string;
}

/**
 * Test Trends Chart Component
 * Displays historical trends for a specific test component
 */
export const TestTrendsChart: React.FC<TestTrendsChartProps> = ({
  componentName,
  historicalData,
  unit
}) => {
  // Format data for the chart
  const chartData = historicalData.map(item => ({
    date: format(parseISO(item.date), 'MMM d'),
    value: item.value,
    referenceMin: item.referenceMin,
    referenceMax: item.referenceMax
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{componentName} Trend</CardTitle>
        {unit && <CardDescription>Values in {unit}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
              stroke="var(--border-subtle)"
            />
            <YAxis
              tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
              stroke="var(--border-subtle)"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--background-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '6px'
              }}
              labelStyle={{ color: 'var(--text-primary)' }}
            />
            <Legend />

            {/* Reference range lines */}
            {chartData[0]?.referenceMin !== undefined && (
              <Line
                type="monotone"
                dataKey="referenceMin"
                stroke="var(--status-warning)"
                strokeDasharray="5 5"
                dot={false}
                name="Min Reference"
              />
            )}
            {chartData[0]?.referenceMax !== undefined && (
              <Line
                type="monotone"
                dataKey="referenceMax"
                stroke="var(--status-warning)"
                strokeDasharray="5 5"
                dot={false}
                name="Max Reference"
              />
            )}

            {/* Actual values */}
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--brand-primary)"
              strokeWidth={2}
              dot={{ fill: 'var(--brand-primary)', r: 4 }}
              name="Value"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default TestTrendsChart;
